/**
 * taskOwner.ts — owner / assignee classification for the task board.
 *
 * BOARD-TEAM-OWNERSHIP-UX-1: a small team of humans plus a fleet of agents
 * shares the board. Tenant filtering is not the same as owner filtering.
 * The card chip and the board-level filter both need to answer "who owns this
 * row, and is that an actor I can ping or an agent I can dispatch?"
 *
 * Source-of-truth fields on the engine `TaskItem`:
 *   - `assigned_to`         free-form handle: "claude-ide", "codex", "director",
 *                           a person's email/handle, or null. Set by the engine
 *                           when a task is dispatched OR by Director edits.
 *   - `assigned_user_id`    UUID FK to the engine `users` table — populated
 *                           only when the assignee is a real CES user (not an
 *                           agent identifier). Definitive human signal.
 *   - `last_actor_type`     'human' | 'agent' | 'system' for the most-recent
 *                           mutation. NOT the same as the assignee — a Director
 *                           can edit a task assigned to claude-ide and
 *                           last_actor_type flips to 'human' even though the
 *                           OWNER is still the agent.
 *
 * Heuristic for owner classification:
 *   1. No assigned_to             → 'unassigned'
 *   2. assigned_user_id present   → 'human' (definitive)
 *   3. Handle matches AGENT_RE    → 'agent'
 *   4. Handle === assigned to the
 *      same name as last_actor_id
 *      AND last_actor_type='agent'→ 'agent'
 *   5. Else                       → 'human' (default; we'd rather under-classify
 *                                  agents than mis-tag a real teammate as a bot)
 */
import type { TaskItem } from '@/api/types';

export type OwnerKind = 'human' | 'agent' | 'unassigned';

export interface TaskOwner {
  kind: OwnerKind;
  /** Display handle. Empty string when kind === 'unassigned'. */
  handle: string;
}

/**
 * Recognized agent handle patterns. Conservative: matches the prefixes the
 * engine, harness, and known agent fleet emit. New agents that don't match
 * here will be classified as 'human' (false negative) rather than be
 * mis-classified as an agent — the reverse mistake is worse for a small team.
 *
 * Patterns (case-insensitive):
 *   - claude-...    : claude-ide, claude-coworker, claude-mobile, claude-bot
 *   - codex(-...)?  : codex, codex-1
 *   - agent-...     : agent-c, agent-1, agent-x
 *   - bot-...       : bot-foo, bot-bar
 *   - harness, harness-...
 *   - governor, governor-...
 *   - cowork, cowork-...
 *   - seity(-...)?  : the user's primary emergent entity (acts as agent here)
 *   - "...-agent"   : trailing -agent
 *   - "...-bot"     : trailing -bot
 */
const AGENT_RE = /^(?:claude(?:[-_].+)?|codex(?:[-_].+)?|agent[-_].+|bot[-_].+|harness(?:[-_].+)?|governor(?:[-_].+)?|cowork(?:[-_].+)?|seity(?:[-_].+)?)$|[-_](?:agent|bot)$/i;

/**
 * Classify a single task's owner.
 *
 * Always returns a value — callers don't need to handle null. For an
 * unassigned task, `handle` is an empty string and `kind` is 'unassigned'.
 */
export function getTaskOwner(task: TaskItem): TaskOwner {
  const raw = (task.assigned_to ?? '').trim();
  if (!raw) {
    return { kind: 'unassigned', handle: '' };
  }
  if (task.assigned_user_id) {
    return { kind: 'human', handle: raw };
  }
  if (AGENT_RE.test(raw)) {
    return { kind: 'agent', handle: raw };
  }
  // Cross-check: if the assigned handle exactly matches the most-recent actor
  // id AND that actor reported itself as an agent, classify as agent. This
  // catches custom agent names (vertical_pipeline, ingest_worker) that don't
  // match AGENT_RE but identify themselves correctly through the actor system.
  const actorId = (task.last_actor_id ?? '').trim();
  if (
    actorId &&
    actorId.toLowerCase() === raw.toLowerCase() &&
    task.last_actor_type === 'agent'
  ) {
    return { kind: 'agent', handle: raw };
  }
  return { kind: 'human', handle: raw };
}

/**
 * Owner filter values used by the board UI. These map to a predicate over
 * `TaskItem` so the filter dropdown can stay flat (one select, several
 * semantic groups).
 *
 *   'all'         — no owner filter
 *   'unassigned'  — assigned_to is null/empty
 *   'humans'      — getTaskOwner(task).kind === 'human'
 *   'agents'      — getTaskOwner(task).kind === 'agent'
 *   '@me'         — only present when the current user has a handle; matches
 *                   tasks whose assigned_to OR assigned_user_id matches the
 *                   viewing user
 *   <handle>      — exact match on assigned_to (case-sensitive — that's how
 *                   the engine stores handles)
 */
export type OwnerFilterValue = 'all' | 'unassigned' | 'humans' | 'agents' | '@me' | string;

export interface OwnerFilterContext {
  /** The current user's display name / handle, if known. */
  meHandle?: string | null;
  /** The current user's UUID (matches `assigned_user_id`), if known. */
  meUserId?: string | null;
}

/**
 * Build a predicate for the owner filter. Pure — easy to test, easy to
 * compose with other filters in the board's `useMemo`.
 *
 * Every branch routes through `getTaskOwner(task).handle` so the predicate
 * cannot disagree with the chip / dropdown grouping. In particular:
 *   - whitespace-only `assigned_to` ("   ") is unassigned everywhere
 *   - per-handle filters compare on the trimmed handle so trailing-space
 *     storage variants ("director ") match the dropdown's "@director"
 */
export function ownerFilterPredicate(
  value: OwnerFilterValue,
  ctx: OwnerFilterContext = {},
): (task: TaskItem) => boolean {
  if (value === 'all') return () => true;
  if (value === 'unassigned') return (t) => getTaskOwner(t).kind === 'unassigned';
  if (value === 'humans') return (t) => getTaskOwner(t).kind === 'human';
  if (value === 'agents') return (t) => getTaskOwner(t).kind === 'agent';
  if (value === '@me') {
    const handle = (ctx.meHandle ?? '').trim().toLowerCase();
    const userId = (ctx.meUserId ?? '').trim();
    return (t) => {
      if (userId && t.assigned_user_id === userId) return true;
      const owner = getTaskOwner(t);
      if (handle && owner.handle.toLowerCase() === handle) return true;
      return false;
    };
  }
  // Specific handle — engine-stored case is preserved on `handle` (we just
  // trim, never lowercase, to keep "@Director" and "@director" distinct
  // operators if the engine ever distinguishes them). Compare normalized to
  // normalized so a stored "director " still matches "@director".
  const target = value.trim();
  return (t) => getTaskOwner(t).handle === target;
}

/**
 * Group the unique handles found across a task list into humans + agents,
 * each sorted alphabetically. Used to populate the filter dropdown without
 * duplicating heuristic logic at the call site.
 */
export interface OwnerGroups {
  humans: string[];
  agents: string[];
  /** True when at least one task has no assignee. */
  hasUnassigned: boolean;
}

export function groupOwners(tasks: TaskItem[]): OwnerGroups {
  const humans = new Set<string>();
  const agents = new Set<string>();
  let hasUnassigned = false;

  for (const t of tasks) {
    const o = getTaskOwner(t);
    if (o.kind === 'unassigned') {
      hasUnassigned = true;
      continue;
    }
    if (o.kind === 'agent') agents.add(o.handle);
    else humans.add(o.handle);
  }
  return {
    humans: Array.from(humans).sort((a, b) => a.localeCompare(b)),
    agents: Array.from(agents).sort((a, b) => a.localeCompare(b)),
    hasUnassigned,
  };
}
